import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import { commonShiftsRoute } from 'api';
import { fetchJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { COMMON_SHIFT_RECOMMENDATION_RANKS } from 'features/scheduleBuilder/ScheduleBuilderView/ShiftForm/constants';

type CommonShift = {
  id: string;
  start_at: string;
  end_at: string;
  color: string;
  parent_shift_id: string;
  role_id: string;
  role_name: string;
  day_of_week: string;
  recommendation_rank: string;
  level: number;
};

type CommonShiftsResponse = CommonShift[];

type FetchCommonShiftParams = {
  dayOfWeek: string;
  jobId?: string;
  locationId?: string;
};

interface CommonShiftsState extends EntityState<CommonShift> {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | undefined;
}

const commonShiftsAdapter = createEntityAdapter<CommonShift>({
  sortComparer: (a, b) =>
    COMMON_SHIFT_RECOMMENDATION_RANKS.indexOf(a.recommendation_rank) -
    COMMON_SHIFT_RECOMMENDATION_RANKS.indexOf(b.recommendation_rank),
});

const initialState: CommonShiftsState = commonShiftsAdapter.getInitialState({
  status: 'idle',
  error: undefined,
});

export const fetchCommonShifts = createAsyncThunk<
  CommonShiftsResponse,
  FetchCommonShiftParams
>('commonShifts/fetchCommonShifts', async (params: FetchCommonShiftParams) => {
  const response: Promise<CommonShiftsResponse> = await fetchJSON(
    commonShiftsRoute({
      jobId: params.jobId,
      dayOfWeek: params.dayOfWeek,
      locationId: params.locationId,
    })
  );

  const data = await response;
  return data;
});

const commonShiftsSlice = createSlice({
  name: 'commonShifts',
  initialState,
  reducers: {
    clearCommonShifts: state => {
      commonShiftsAdapter.removeAll(state);
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCommonShifts.pending, state => {
      state.status = 'loading';
    });
    builder.addCase(fetchCommonShifts.fulfilled, (state, action) => {
      const sorted = action.payload.sort(
        (a: CommonShift, b: CommonShift) =>
          COMMON_SHIFT_RECOMMENDATION_RANKS.indexOf(a.recommendation_rank) -
          COMMON_SHIFT_RECOMMENDATION_RANKS.indexOf(b.recommendation_rank)
      );

      commonShiftsAdapter.setAll(state, sorted);
      state.status = 'succeeded';
    });
    builder.addCase(fetchCommonShifts.rejected, (state, action) => {
      // need to handle error from backend
      state.error = action.error.message;
      state.status = 'failed';
    });
  },
});

const { selectAll } = commonShiftsAdapter.getSelectors();

export const selectCommonShiftsStatus = (state: RootState) =>
  state.get('scheduleBuilderSchedule').commonShifts.status;

export const selectCommonShifts = (state: RootState) =>
  selectAll(state.get('scheduleBuilderSchedule').commonShifts);

export const { reducer } = commonShiftsSlice;
export const { clearCommonShifts } = commonShiftsSlice.actions;
